import { CommonModule, Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, Router, RouterOutlet } from '@angular/router';
import { animate, query, style, transition, trigger } from '@angular/animations';
import {
  MsalBroadcastService,
  MsalModule,
  MsalService,
} from '@azure/msal-angular';
import {
  EventMessage,
  EventType,
  InteractionStatus,
} from '@azure/msal-browser';
import { initFlowbite } from 'flowbite';
import { Subject, takeUntil } from 'rxjs';
import { NavbarComponent } from './common/components/navbar/navbar.component';
import { SpinnerComponent } from './common/components/spinner/spinner.component';
import { SpinnerState } from './common/state-stores/spinner.state';
import { PlatformheaderComponent } from './common/components/platformheader/platformheader/platformheader.component';
import { AppScrollableState } from './common/state-stores/app-scrollable.state';
import { AppUpdateService } from './common/services/app-update-service/app-update.service';
import { AppUpdateState } from './common/state-stores/app-update.state';
import { AppDataState } from './common/state-stores/app-data-state.state';
import { environment } from '../environments/environment';
import { BackgroundOperationResultModalComponent } from './common/components/background-operation-result-modal/background-operation-result-modal.component';
import { BackgroundOperationPendingToastComponent } from './common/components/background-operation-pending-toast/background-operation-pending-toast.component';
import { TranslocoModule } from '@jsverse/transloco';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    MsalModule,
    NavbarComponent,
    CommonModule,
    TranslocoModule,
    SpinnerComponent,
    PlatformheaderComponent,
    BackgroundOperationResultModalComponent,
    BackgroundOperationPendingToastComponent,
  ],
  templateUrl: './app.component.html',
  animations: [
    trigger('routeTransition', [
      transition('* <=> *', [
        query(
          ':enter',
          [
            style({
              opacity: 0,
              transform: 'translate3d(0, 8px, 0) scale(0.995)',
              filter: 'blur(2px)',
            }),
            animate(
              '420ms cubic-bezier(0.16, 1, 0.3, 1)',
              style({
                opacity: 1,
                transform: 'translate3d(0, 0, 0) scale(1)',
                filter: 'blur(0)',
              })
            ),
          ],
          { optional: true }
        ),
      ]),
    ]),
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'WaterMain';
  private readonly _destroying$ = new Subject<void>();
  loginDisplay = false;
  showDetailForm = false; // or set this based on your logic
  isNavbarCollapsed = false;

  reloadApp() {
    this.appUpdateState.hideUpdateBanner(); // Hide the banner after reload
    window.location.reload();
  }

  constructor(
    private readonly authService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly router: Router,
    private readonly appState: AppDataState,
    protected readonly spinnerState: SpinnerState,
    private readonly location: Location,
    protected readonly appScrollableState: AppScrollableState,
    private appUpdateService: AppUpdateService,
    public appUpdateState: AppUpdateState,
  ) {}

  onNavbarToggled(isCollapsed: boolean) {
    this.isNavbarCollapsed = isCollapsed;
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount() {
    let activeAccount = this.authService.instance.getActiveAccount();

    if (
      !activeAccount &&
      this.authService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  private scheduleFlowbiteInit(): void {
    const runInit = () => initFlowbite();
    const requestIdle = (window as Window & {
      requestIdleCallback?: (cb: () => void) => number;
    }).requestIdleCallback;

    if (requestIdle) {
      requestIdle(runInit);
      return;
    }

    setTimeout(runInit, 0);
  }

  async ngOnInit(): Promise<void> {
    this.appState.setCurrencyCode(environment.currencyCode ?? 'CAD');

    this.appUpdateService.startCheckingForUpdates();

    // Lock screen orientation to portrait on mobile devices
    this.lockOrientationToPortrait();

    this.scheduleFlowbiteInit();

    if (!this.location.path().includes('stationapp')) {
      this.appState.setStationAppConfigPermissionPresent(false);
    }


    if (sessionStorage.getItem('lastSpinnerState') === 'true') {
      this.spinnerState.showSpinner();
    } else {
      this.spinnerState.hideSpinner();
      sessionStorage.removeItem('lastSpinnerState');
    }

    this.authService.handleRedirectObservable().subscribe();
    this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window

    this.msalBroadcastService.msalSubject$
      .pipe(takeUntil(this._destroying$))
      .subscribe((result: EventMessage) => {
        if (result.eventType === EventType.LOGIN_SUCCESS) {
          this.checkAndSetActiveAccount();
          this.setLoginDisplay();
          if (this.location.path().includes('apply')) {
            this.loginDisplay = false;
            return;
          }
          if (this.authService.instance.getAllAccounts().length === 0) {
            this.router.navigate(['/login']);
          } else if (
            this.location.path() === '/' ||
            this.location.path() === '/login' ||
            this.location.path() === '' ||
            this.location.path().includes('portal')
          ) {
            this.router.navigate(['platform/home']);
          }

          this.spinnerState.hideSpinner();
          sessionStorage.removeItem('lastSpinnerState');
        } else if (result.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
          sessionStorage.clear();
          this.loginDisplay = false;
          this.router.navigate(['/login']);
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(takeUntil(this._destroying$))
      .subscribe((status: InteractionStatus) => {
        if (status === InteractionStatus.Login) {
          this.spinnerState.showSpinner();
          sessionStorage.setItem(
            'lastSpinnerState',
            this.spinnerState.loading().toString()
          );
        } else if (status === InteractionStatus.None) {
          this.checkAndSetActiveAccount();
          this.setLoginDisplay();
          if (this.location.path().includes('apply')) {
            this.loginDisplay = false;
            return;
          }
          if (this.authService.instance.getAllAccounts().length === 0) {
            if (!this.location.path().includes('portal')) {
              this.router.navigate(['/login']);
            }
          } else if (
            this.location.path() === '/' ||
            this.location.path() === '/login' ||
            this.location.path() === '' ||
            this.location.path().includes('portal')
          ) {
            this.router.navigate(['platform/home']);
          }

          this.spinnerState.hideSpinner();
          sessionStorage.removeItem('lastSpinnerState');
        }
      });

    this.router.events
      .pipe(takeUntil(this._destroying$))
      .subscribe((event) => {
        if (!this.spinnerState.isAppLaunchSpinnerActive()) {
          return;
        }

        if (event instanceof NavigationEnd) {
          if (this.isAppHomeUrl(event.urlAfterRedirects.toLowerCase())) {
            this.spinnerState.hideAppLaunchSpinner();
          }
          return;
        }

        if (event instanceof NavigationCancel || event instanceof NavigationError) {
          this.spinnerState.hideAppLaunchSpinner();
        }
      });
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  private lockOrientationToPortrait(): void {
    // Check if the device is mobile and if the Screen Orientation API is available
    if (this.isMobileDevice() && 'orientation' in screen && 'lock' in screen.orientation) {
      const orientation = screen.orientation as any;
      orientation.lock('portrait').catch((error: any) => {
        console.warn('Screen orientation lock failed:', error);
      });
    }
  }

  private isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  private isAppHomeUrl(url: string): boolean {
    return /^\/[^/]+\/home(?:$|[/?#])/.test(url) && !url.startsWith('/platform/home');
  }

  getRouteAnimationState(outlet: RouterOutlet): string {
    if (!outlet || !outlet.isActivated) {
      return 'root';
    }

    return outlet.activatedRoute.routeConfig?.path ?? 'root';
  }
}
